import React, { useState } from "react";
import TextLink from "components/common/TextLink";
import TextLinkV2 from "components/common/TextLinkV2";
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image";

import {
  cameraDaysClass,
  cameraImageClass,
  cameraImageContainer,
  cameraInfoContainer,
  cameraPriceClass,
  cardContainer,
  circleButtonSelectedClass,
  circleButtonUnselectedClass,
  licenseTypeButtonsClass,
  licenseTypeContainerClass,
  lineClass,
  resolutionText,
  smallFontClass,
  tooltipClass,
  tooltipContainer,
  tooltipIconClass,
  tooltipTextClass,
  typeButtonSelectedClass,
  typeButtonUnselectedClass,
  yearsButtonsClass,
  yearsContainerClass,
} from "../styles/pricingStyles";

export default function RelayCard({ info }) {
  const {
    name,
    title,
    resolution,
    price,
    image,
    altText,
    storage,
    licenseOptions = [],
  } = info;

  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(0);
  const [selectedTermIndex, setSelectedTermIndex] = useState(0);

  const selectedLicense = licenseOptions[selectedLicenseIndex];
  const selectedTerm = selectedLicense?.terms?.[selectedTermIndex];
  const subtotal = selectedTerm?.price ?? 0;
  const total = price + subtotal;

  return (
    <div className={cardContainer} id={name.toLowerCase()}>
      {/* Image Section */}
      <div className={cameraImageContainer}>
        <GatsbyImage
          image={getImage(image)}
          className={cameraImageClass}
          alt={altText ?? name}
          objectFit="contain"
        />
      </div>

      {/* Info Section */}
      <div className={cameraInfoContainer}>
        {/* Header */}
        <div>
          <h3 className="heavy">
            {name} {title} <span className={resolutionText}>{resolution}</span>
          </h3>
          <TextLinkV2 path={info.link} target="_blank" title={"View Specs"} />
        </div>

        {/* Storage */}
        <div>
          <SectionWithTooltip
            title="Storage"
            tooltipText="Up to 10 cameras per N100 and up to 20 days of storage per camera."
          ></SectionWithTooltip>
          <div className={cameraDaysClass}>
            <button className={circleButtonSelectedClass}>{storage}</button>
          </div>
        </div>

        {/* License */}
        <div className={licenseTypeContainerClass}>
          <SectionWithTooltip
            title="License"
            tooltipText={
              <>
                AI-features and cloud archiving not included in the Professional
                License. For a detailed comparison, visit our{" "}
                <TextLink to="/license-comparison/">
                  License Comparison
                </TextLink>{" "}
                page.
              </>
            }
          >
            {!!subtotal && (
              <div
                className={cameraPriceClass}
              >{`$${subtotal.toLocaleString()}`}</div>
            )}
          </SectionWithTooltip>

          <div className={licenseTypeButtonsClass} style={{ flexWrap: "wrap" }}>
            {licenseOptions.map((license, index) => (
              <button
                key={index}
                className={
                  index === selectedLicenseIndex
                    ? typeButtonSelectedClass
                    : typeButtonUnselectedClass
                }
                onClick={() => {
                  setSelectedLicenseIndex(index);
                  setSelectedTermIndex(0); // Reset terms when license changes
                }}
              >
                {license.name}
              </button>
            ))}
          </div>
        </div>

        {/* Years */}
        {selectedLicense?.terms && (
          <div className={yearsContainerClass}>
            <h3>Years</h3>
            <div className={yearsButtonsClass}>
              {selectedLicense.terms.map((term, index) => (
                <button
                  key={index}
                  className={
                    index === selectedTermIndex
                      ? circleButtonSelectedClass
                      : circleButtonUnselectedClass
                  }
                  onClick={() => setSelectedTermIndex(index)}
                >
                  {term.term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Total */}
        <div>
          <div className={lineClass}>
            <h3 className="heavy">Total Cost</h3>
            <h3>{`$${total.toLocaleString()}`}</h3>
          </div>
          <div className={lineClass}>
            <p className={smallFontClass}>Includes hardware and license</p>
            <p className={smallFontClass}>
              <strong>(MSRP USD)</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionWithTooltip({ title, tooltipText, children }) {
  return (
    <div className={lineClass}>
      <div className={tooltipContainer}>
        <h3>{title}</h3>
        <div className={tooltipClass}>
          <StaticImage
            src="../img/tooltip-icon.png"
            className={tooltipIconClass}
            alt={`${title} Tooltip`}
            objectFit="contain"
          />
          <span className={tooltipTextClass}>{tooltipText}</span>
        </div>
      </div>
      {children}
    </div>
  );
}
